function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { uniqueId } from 'lodash';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import * as IconSet from "../../../../icons/index.js";
import Badge from "../../../basic/Badge/Badge.js";
import Button from "../../../basic/Button/Button.js";
import Icon from "../../../basic/Icon/Icon.js";
import Tooltip from "../../../basic/Tooltip/Tooltip.js";
import { getAdjustedSize, getDescriptionMarginTop, getFieldSize, getLineHeight } from "../helpers.js";
import DynamicItem from "./DynamicItem.js";
import { StyledHeader, StyledHeading } from "./shared.js";
var StyledInfoIconWrapper = styled.div.withConfig({
  componentId: "CL__sc-jxsqgz-0"
})(["align-items:center;display:flex;height:", ";"], _ref => {
  var {
    size
  } = _ref;
  return getLineHeight(size);
});
var StyledDescription = styled.p.withConfig({
  componentId: "CL__sc-jxsqgz-1"
})(["color:var(--text-color-dim);margin-top:", ";"], _ref2 => {
  var {
    size
  } = _ref2;
  return getDescriptionMarginTop(size);
});
var ViewListState = _ref3 => {
  var {
    addButtonText,
    description,
    duplicateItem,
    EditItemForm,
    headingBadge,
    headingInfo,
    id,
    items: itemsWithOptionalIndex,
    maxItems,
    minItems = 0,
    onItemAdd,
    onItemDelete,
    onItemEdit,
    onEdit,
    onItemsChange,
    size,
    subject,
    title,
    titleAs
  } = _ref3;
  var items = useMemo(() => itemsWithOptionalIndex.map(item => {
    var _item$id;
    return _objectSpread(_objectSpread({}, item), {}, {
      id: (_item$id = item.id) !== null && _item$id !== void 0 ? _item$id : uniqueId("".concat(id, "-"))
    });
  }), [itemsWithOptionalIndex]);

  // This determines if the "Add" button should be displayed
  var canAdd = (!!EditItemForm || !!onItemAdd) && (!maxItems || items.length < maxItems);

  // This determines if the "Edit" button should be displayed
  var canEdit = !!EditItemForm || !!onItemEdit;

  // This determines if the "Duplicate" button should be displayed
  var canDuplicate = !!duplicateItem && (!maxItems || items.length < maxItems);

  // This determines if the "Delete" button should be displayed
  var canDelete = items.length > minItems;

  // Called when the user clicks the "Add" button
  var handleAdd = () => {
    if (onItemAdd) onItemAdd(); // If the user provided a callback for the "Add" button, call it
    if (EditItemForm) onEdit(items.length); // If the user provided a form component to edit items inside the DynamicList, open it
  };

  // Called when the user clicks the "Edit" button in an item
  var handleEdit = index => {
    if (onItemEdit && items[index]) onItemEdit(items[index], index); // If the user provided a callback for the "Edit" button, call it
    if (EditItemForm) onEdit(index); // If the user provided a form component to edit items inside the DynamicList, open it
  };

  // Called when the user clicks the "Delete" button in an item
  var handleDelete = index => {
    if (onItemDelete && items[index]) onItemDelete(items[index], index); // If the user provided a callback for the "Delete" button, call it
    var newItems = [...items];
    newItems.splice(index, 1);
    onItemsChange(newItems);
  };

  // Called when the user clicks the "Duplicate" button in an item
  var handleDuplicate = index => {
    var itemtoDuplicate = itemsWithOptionalIndex[index];
    if (!itemtoDuplicate || !duplicateItem) return;
    var newItem = duplicateItem(itemtoDuplicate, index);
    var newItems = [...itemsWithOptionalIndex];
    newItems.splice(index + 1, 0, newItem);
    onItemsChange(newItems);
  };

  // Called when the user finishes dragging an item
  var handleDragEnd = event => {
    var {
      active,
      over
    } = event;
    if (!over || active.id === over.id) return;
    var oldIndex = items.findIndex(item => item.id === active.id);
    var newIndex = items.findIndex(item => item.id === over.id);
    var newItems = arrayMove(items, oldIndex, newIndex);
    onItemsChange(newItems);
  };
  var sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: {
      distance: 10
    }
  }));
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(StyledHeader, null, /*#__PURE__*/React.createElement(StyledHeading, {
    forwardedAs: titleAs
  }, title), headingInfo && items.length && /*#__PURE__*/React.createElement(StyledInfoIconWrapper, {
    size: size
  }, /*#__PURE__*/React.createElement(Icon, {
    component: IconSet.Information,
    "data-for": "".concat(id, "-tooltip}"),
    "data-tip": true,
    size: getAdjustedSize(size)
  }), /*#__PURE__*/React.createElement(Tooltip, {
    id: "".concat(id, "-tooltip}"),
    size: getAdjustedSize(size)
  }, headingInfo)), headingBadge && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Badge, _extends({
    size: getAdjustedSize(size)
  }, headingBadge)))), description && !items.length && /*#__PURE__*/React.createElement(StyledDescription, {
    size: size
  }, description)), !!items.length && /*#__PURE__*/React.createElement("ol", null, /*#__PURE__*/React.createElement(DndContext, {
    collisionDetection: closestCenter,
    modifiers: [restrictToVerticalAxis],
    onDragEnd: handleDragEnd,
    sensors: sensors
  }, /*#__PURE__*/React.createElement(SortableContext, {
    items: items,
    strategy: verticalListSortingStrategy
  }, items.map((item, index) => /*#__PURE__*/React.createElement(DynamicItem, {
    canDelete: canDelete,
    canDuplicate: canDuplicate,
    canEdit: canEdit,
    item: item,
    key: item.id,
    onDelete: () => handleDelete(index),
    onDuplicate: () => handleDuplicate(index),
    onEdit: () => handleEdit(index),
    size: size,
    subject: subject
  }))))), canAdd && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Button, {
    icon: IconSet.Plus,
    onClick: handleAdd,
    size: getFieldSize(size),
    variant: "text"
  }, addButtonText || "Add ".concat(subject))));
};
export default ViewListState;